import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Users, FolderOpen, FileText, TrendingUp, LifeBuoy, Loader2, CheckCircle2, HeartHandshake, Inbox, Send, Wand2,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";

const fmt = (n) => (n || 0).toLocaleString();
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
};

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-border bg-card p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl bg-primary/12 grid place-items-center shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div className="min-w-0">
        <div className="font-display text-2xl font-bold leading-none">{value}</div>
        <div className="text-xs text-muted-foreground mt-1 truncate">{label}</div>
      </div>
    </div>
  );
}

function AiToolsToggle() {
  const { user, patchUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const on = user?.ai_tools_enabled !== false;
  const flip = async (v) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", { ai_tools_enabled: v });
      patchUser({ ai_tools_enabled: data.ai_tools_enabled });
      toast.success(v ? "AI generators are visible again" : "AI generators hidden for everyone");
    } catch { toast.error("Couldn't save that setting"); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="admin-ai-tools-card" className="rounded-2xl border border-border bg-card p-5 mb-8 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="font-semibold flex items-center gap-2"><Wand2 className="w-4 h-4 text-primary" /> AI generators</div>
        <p className="text-xs text-muted-foreground mt-1">
          Shows or hides Auto-write, Generate draft, Rewrite, Polish, Style brief, Gore pass, Spark, Proofread, Tidy repetition, Continuity check and Repeated chapters for every account, including yours. Codex AI Scan and Import always stay visible.
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {saving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        <span className="text-xs font-medium text-muted-foreground" data-testid="admin-ai-tools-state">{on ? "Shown" : "Hidden"}</span>
        <Switch checked={on} onCheckedChange={flip} disabled={saving} data-testid="admin-ai-tools-switch" />
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("users");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [support, setSupport] = useState([]);
  const [donations, setDonations] = useState([]);
  const [busy, setBusy] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [sending, setSending] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const [s, u, m, d] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/support"),
        api.get("/admin/donations"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setSupport(m.data);
      setDonations(d.data);
    } catch {
      toast.error("Couldn't load admin data");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const resolve = async (id) => {
    setSupport((ms) => ms.map((m) => (m.id === id ? { ...m, status: "resolved", unread_by_admin: false } : m)));
    try { await api.put(`/admin/support/${id}/resolve`); toast.success("Marked resolved"); }
    catch { toast.error("Couldn't update"); load(); }
  };

  const sendReply = async (id) => {
    const body = (drafts[id] || "").trim();
    if (!body) return;
    setSending(id);
    try {
      await api.post(`/admin/support/${id}/reply`, { body });
      setDrafts((d) => ({ ...d, [id]: "" }));
      toast.success("Reply sent");
      await load();
    } catch { toast.error("Couldn't send reply"); }
    finally { setSending(null); }
  };

  if (loading || user === null) {
    return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }
  if (!user || user.role !== "admin") return <Navigate to="/app" replace />;

  const openCount = support.filter((m) => m.status === "open").length;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header back />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-page">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-xl bg-primary grid place-items-center">
            <TrendingUp className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Admin</h1>
            <p className="text-sm text-muted-foreground">Your private overview of CodexStudio.</p>
          </div>
        </div>

        <AiToolsToggle />

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard testid="stat-users" icon={Users} label="Writers signed up" value={busy ? "…" : fmt(stats?.total_users)} />
          <StatCard testid="stat-projects" icon={FolderOpen} label="Books in progress" value={busy ? "…" : fmt(stats?.total_projects)} />
          <StatCard testid="stat-words" icon={FileText} label="Words written" value={busy ? "…" : fmt(stats?.total_words)} />
          <StatCard testid="stat-new" icon={TrendingUp} label="New this week" value={busy ? "…" : fmt(stats?.new_users_this_week)} />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-5 border-b border-border">
          {[
            { k: "users", label: "Writers", icon: Users },
            { k: "support", label: `Support${openCount ? ` (${openCount})` : ""}`, icon: LifeBuoy },
            { k: "donations", label: "Donations", icon: HeartHandshake },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`admin-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`flex items-center gap-2 px-3 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                tab === t.k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {busy ? (
          <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : tab === "users" ? (
          <div className="rounded-2xl border border-border overflow-hidden" data-testid="admin-users-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-muted-foreground">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-semibold">Writer</th>
                    <th className="px-4 py-3 font-semibold">Email</th>
                    <th className="px-4 py-3 font-semibold text-right">Projects</th>
                    <th className="px-4 py-3 font-semibold text-right">Words</th>
                    <th className="px-4 py-3 font-semibold">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`admin-user-row`} className="border-t border-border hover:bg-secondary/30">
                      <td className="px-4 py-3">
                        <div className="font-medium flex items-center gap-2">
                          {u.pen_name || u.name || "Writer"}
                          {u.role === "admin" && <Badge variant="secondary" className="text-[10px]">admin</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                      <td className="px-4 py-3 text-right">{fmt(u.project_count)}</td>
                      <td className="px-4 py-3 text-right">{fmt(u.word_count)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{fmtDate(u.created_at)}</td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No writers yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : tab === "support" ? (
          <div className="space-y-3" data-testid="admin-support-list">
            {support.length === 0 && (
              <div className="rounded-2xl border border-border bg-card p-10 text-center text-muted-foreground flex flex-col items-center gap-3">
                <Inbox className="w-8 h-8 text-muted-foreground/50" /> No support messages yet.
              </div>
            )}
            {support.map((m) => (
              <div key={m.id} data-testid="admin-support-item" className={`rounded-2xl border p-5 ${m.unread_by_admin ? "border-primary/50 bg-card" : "border-border bg-card/50"}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{m.subject || "(no subject)"}</span>
                      <Badge variant={m.status === "open" ? "default" : "secondary"} className="text-[10px]">{m.status}</Badge>
                      {m.unread_by_admin && <Badge className="text-[10px] bg-primary/15 text-primary">new</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {m.user_name ? `${m.user_name} · ` : ""}{m.user_email} · {fmtDate(m.created_at)}
                    </div>
                  </div>
                  {m.status === "open" && (
                    <Button size="sm" variant="outline" onClick={() => resolve(m.id)} data-testid={`resolve-${m.id}`} className="gap-1.5 shrink-0">
                      <CheckCircle2 className="w-4 h-4" /> Resolve
                    </Button>
                  )}
                </div>

                <div className="space-y-2.5 mt-4">
                  {(m.messages || []).map((msg) => {
                    const admin = msg.author === "admin";
                    return (
                      <div key={msg.id} className={`flex ${admin ? "justify-end" : "justify-start"}`} data-testid={`admin-bubble-${msg.author}`}>
                        <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${admin ? "bg-primary text-primary-foreground rounded-tr-sm" : "bg-secondary text-foreground rounded-tl-sm"}`}>
                          <div className={`text-[10px] font-semibold mb-0.5 ${admin ? "text-primary-foreground/80" : "text-primary"}`}>
                            {admin ? "You" : (m.user_name || "Writer")}
                          </div>
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.body}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-end gap-2 mt-4">
                  <Textarea rows={2} value={drafts[m.id] || ""} onChange={(e) => setDrafts((d) => ({ ...d, [m.id]: e.target.value }))}
                    placeholder="Write a reply to this writer…" data-testid={`admin-reply-input-${m.id}`} className="resize-none" />
                  <Button size="icon" onClick={() => sendReply(m.id)} disabled={sending === m.id || !(drafts[m.id] || "").trim()}
                    data-testid={`admin-reply-send-${m.id}`} className="shrink-0 h-10 w-10">
                    {sending === m.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div data-testid="admin-donations">
            {stats && (
              <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5 mb-4 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-primary/15 grid place-items-center shrink-0">
                  <HeartHandshake className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="font-display text-2xl font-bold leading-none">£{(((stats.donations_total_pence || 0)) / 100).toFixed(2)}</div>
                  <div className="text-xs text-muted-foreground mt-1">raised from {stats.donations_count || 0} donation{(stats.donations_count === 1) ? "" : "s"}</div>
                </div>
              </div>
            )}
            {donations.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center">
                <HeartHandshake className="w-10 h-10 text-primary/50 mx-auto mb-4" />
                <h3 className="font-display text-xl font-bold mb-1.5">No donations yet</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  When a reader supports CodexStudio, it'll show up here with their name, amount and date.
                </p>
              </div>
            ) : (
              <div className="rounded-2xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/50 text-muted-foreground">
                      <tr className="text-left">
                        <th className="px-4 py-3 font-semibold">Donor</th>
                        <th className="px-4 py-3 font-semibold">Email</th>
                        <th className="px-4 py-3 font-semibold text-right">Amount</th>
                        <th className="px-4 py-3 font-semibold">Status</th>
                        <th className="px-4 py-3 font-semibold">When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {donations.map((d) => (
                        <tr key={d.id} data-testid="donation-row" className="border-t border-border hover:bg-secondary/30">
                          <td className="px-4 py-3 font-medium">{d.donor_name || "Anonymous"}</td>
                          <td className="px-4 py-3 text-muted-foreground">{d.donor_email || "—"}</td>
                          <td className="px-4 py-3 text-right font-semibold">£{((d.amount_pence || 0) / 100).toFixed(2)}</td>
                          <td className="px-4 py-3">
                            <Badge variant={d.status === "COMPLETED" ? "default" : "secondary"} className="text-[10px]">{(d.status || "").toLowerCase()}</Badge>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{fmtDate(d.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
